import {
  get as networkGet,
  patch as networkPatch,
  put as networkPut
} from '../../networkClient';
import { Path } from '../path';
import { getCookie } from '../../../utils';

// REST returns snake_case; convert to camelCase at the fetch boundary so
// consumers stay in the existing camelCase shape.
const toCamel = key => key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
const camelizeKeys = value => {
  if (Array.isArray(value)) return value.map(camelizeKeys);
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[toCamel(k)] = camelizeKeys(v);
    }
    return out;
  }
  return value;
};

// JWT payload contains the uid. We decode client-side to call /users/:uid
// without adding a /users/me alias; the GraphQL `getUser` resolver got the uid
// from the same token server-side.
const getUidFromToken = () => {
  try {
    const cookie = getCookie('rb_access_token');
    if (!cookie) return null;
    const token = cookie.replace(/^Bearer\s+/i, '');
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.uid || payload.sub || null;
  } catch {
    return null;
  }
};

// Returns { data } on success, { error, status, message } on failure.
// status 401 lets callers drive the existing logout flow.
export async function getCurrentUser() {
  const uid = getUidFromToken();
  if (!uid) {
    return {
      error: true,
      status: 401,
      message: 'Missing or invalid auth token'
    };
  }
  const response = await networkGet(
    Path.USER_BY_ID(uid),
    null,
    null,
    true,
    true
  );
  if (response?.status >= 200 && response?.status < 300 && response?.data) {
    return { data: camelizeKeys(response.data) };
  }
  // networkGet swallows axios errors and returns the error object itself.
  return {
    error: true,
    status: response?.response?.status ?? response?.status ?? 500,
    message:
      response?.response?.data?.error ||
      response?.message ||
      'Failed to fetch user'
  };
}

// Self-edit: server identifies the user from the auth token.
// status must be one of 'online' | 'offline' | 'onbreak'.
export async function updateAgentStatus(status) {
  try {
    const response = await networkPatch(
      Path.USER_ME_STATUS,
      { status },
      null,
      null,
      true,
      true
    );
    return response?.data;
  } catch (error) {
    return {
      error: true,
      message: error.response?.data?.error || error.message,
      status: error.response?.status
    };
  }
}

// Self-edit: server identifies the user from the auth token, no uid needed.
export async function updateUserNotes(notes) {
  try {
    const response = await networkPut(
      Path.USER_ME_NOTES,
      { notes },
      null,
      null,
      true,
      true
    );
    return response?.data;
  } catch (error) {
    return {
      error: true,
      message: error.response?.data?.error || error.message,
      status: error.response?.status
    };
  }
}
